import { useEffect, useReducer, useState } from 'react';

import { Accordion } from '@/components/core/Accordion';
import { Button } from '@/components/core/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault
} from '@/components/core/Dialog';
import { Loader } from '@/components/core/Loader';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/core/Select';
import { FacetedFilterOption, filtersReducer } from '@/components/FacetedFilter';
import { LOCAL_STORAGE } from '@/constants';
import { useDataModel } from '@/hooks/useDataModel';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { ItemsQuery } from '@/types/ItemsQuery';

import { ItemsMedia } from './components/ItemsMedia';
import { ItemsMediaEmpty } from './components/ItemsMediaEmpty';
import { Toolbar } from './components/Toolbar';
import { useCreateSelection, useNamingPatterns, useRunExport, useSelectedItems } from './export-media-asset.queries';
import { GalleryMode, ItemAttribute, ItemsSelectionResponse } from './export-media-asset.types';

interface ExportMediaAssetDialogProps {
    items?: string[] | 'ALL';
    itemsSelection?: { itemsQuery: ItemsQuery };
    onClose: (toWait?:boolean) => void;

}

const ExportMediaAssetDialog = ({ items, itemsSelection, onClose }: ExportMediaAssetDialogProps) => {

    const { t } = useTranslation();
    const { dataModel } = useDataModel();
    const { toast } = useToast();

    const [
        storedFilterAttributes,
        storeFilterAttributes
    ] = useLocalStorage<Record<string, boolean> | undefined>(LOCAL_STORAGE.MEDIA_ASSET_ATTRIBUTE_FILTERS, undefined);
    const [filterAttributes, setFilterAttributes] = useState<Set<string>>(new Set());
    const [selectedFilterAttributes, selectedFilterAttributesDispatch] = useReducer(filtersReducer, []);

    const [open, setOpen] = useState(true);
    const [galleryMode, setGalleryMode] = useState<GalleryMode>(GalleryMode.SMALL);
    const [areAllSelected, setAreAllSelected] = useState(false);
    const [isShowingOnlySelected, setIsShowingOnlySelected] = useState(false);
    const [selectedNamingPattern, setSelectedNamingPattern] = useState('');
    const [openAccordionItems, setOpenAccordionItems] = useState<string[]>([]);

    const [selectionId, setSelectionId] = useState<string | undefined>(undefined);

    const { mutate: createSelection, isPending: isPendingCreateSelection } = useCreateSelection();
    const { mutate: runExport, isPending: isPendingRunExport } = useRunExport();
    const { data: namingPatterns } = useNamingPatterns();
    const { data: selectedItems, isLoading: isLoadingSelectedItems } = useSelectedItems({
        variables: { selectionId },
        enabled: !!selectionId
    });

    const [selectedAssets, setSelectedAssets] = useState<ItemsSelectionResponse>();

    useEffect(() => {

        if (!storedFilterAttributes || !dataModel) {
            return;
        }

        const storedFilterAttributeNames = Object.keys(storedFilterAttributes);
        storedFilterAttributeNames.forEach(attributeName => {
            const filterAttribute = dataModel.attribute(attributeName);

            if (!filterAttribute) {
                return;
            }

            selectedFilterAttributesDispatch({
                type: 'added',
                option: {
                    value: filterAttribute.name,
                    label: filterAttribute.label ?? ''
                }
            });
        });

    }, [storedFilterAttributes, dataModel]);

    useEffect(() => {

        if (!items) {
            return;
        }

        if (items === 'ALL') {

            if (!itemsSelection) {
                return;
            }

            createSelection({
                primaryKeys: itemsSelection
            }, {
                onSuccess: (data) => {
                    setSelectionId(data.selectionId);
                }
            });

        } else {
            createSelection({
                primaryKeys: items
            }, {
                onSuccess: (data) => {
                    setSelectionId(data.selectionId);
                }
            });
        }
    }, [items, itemsSelection, createSelection ]);

    useEffect(() => {

        if (!selectedItems) {
            return;
        }

        const itemPrimaryKeys = Object.keys(selectedItems);

        // Initially, we open up all accordion items
        setOpenAccordionItems(itemPrimaryKeys);

        // We need to extract what attribute are available to
        // extract from, from the selected items.
        // eslint-disable-next-line
        const _attributes: Set<any> = new Set();
        itemPrimaryKeys.forEach(itemPrimaryKey => {
            const item = selectedItems[itemPrimaryKey];
            item.forEach(itemAttribute => {
                // Enriching the item attributes with the item key for ease of use.
                itemAttribute.__itemPrimaryKey = itemPrimaryKey;

                if (itemAttribute.__asset_attribute) {
                    _attributes.add(itemAttribute.__asset_attribute);
                }
            });
        });

        setFilterAttributes(_attributes);

        setSelectedAssets(selectedItems);

    }, [selectedItems]);

    const onAssetSelect = (item: ItemAttribute) => {

        if (!selectedAssets) {
            return;
        }

        const changedItemPrimaryKey = item.__itemPrimaryKey;
        const changedItem =
            selectedAssets[changedItemPrimaryKey].map(itemAttribute => {
                if (itemAttribute.__asset === item.__asset &&
                    itemAttribute.__asset_attribute === item.__asset_attribute &&
                    itemAttribute.__asset_seqno === item.__asset_seqno) { // SeqNo distinguishes between assets having the same url and attribute
                    // this may happen for components of group attributes
                    return {
                        ...itemAttribute,
                        __selected: !itemAttribute.__selected
                    };
                } else {
                    return itemAttribute;
                }
            });

        setSelectedAssets({
            ...selectedAssets,
            [changedItemPrimaryKey]: changedItem
        });

        setAreAllSelected(false);
    };

    const onSelectAll = () => {

        if (!selectedAssets) {
            return;
        }

        const mutatedSelectedAssets: ItemsSelectionResponse = {};
        const itemsPrimaryKeys = Object.keys(selectedAssets);

        itemsPrimaryKeys.forEach(pk => {
            mutatedSelectedAssets[pk] = selectedAssets[pk].map(itemAttribute => ({ ...itemAttribute, __selected: !areAllSelected }));
        });

        setSelectedAssets({
            ...mutatedSelectedAssets
        });

        setAreAllSelected(!areAllSelected);
    };

    const shouldDisplayAttribute = (attribute: ItemAttribute) => {

        // If we have filtration attributes, and the current item attribute is not included
        // in them, then we don't display it, and this have higher precedence on other display conditions.
        if (selectedFilterAttributes.length > 0 &&
            !selectedFilterAttributes.find(filtrationAttribute => filtrationAttribute.value === attribute.__asset_attribute)) {
            return false;
        }

        if (isShowingOnlySelected) {
            return attribute.__selected;
        }

        return true;
    };

    const onExport = () => {

        if (!selectedNamingPattern || !selectedAssets) {
            toast.error('MEDIA_ASSET_EXPORT.NO_PATTERN');
            return;
        }

        const itemsPrimaryKeys = Object.keys(selectedAssets);

        const mutatedSelectedAssets = itemsPrimaryKeys.reduce<{ [key: string]: ItemAttribute[]}>((result, currentPK) => {

            let attributes =
                selectedAssets[currentPK]
                    .filter(itemAttribute => {
                        return itemAttribute.__selected && itemAttribute.__asset_attribute;
                    });

            if (attributes.length === 0) {
                return result;
            }

            // LAX-3143: Setting Amazon sequences AFTER finding out
            // what attributes we are going to export.
            attributes = attributes.map((attribute, index) => {

                attribute.__seq_num = index + 1;

                if (index === 0) {
                    attribute.__seq_amazon = 'MAIN';
                } else if (index < 100) {
                    attribute.__seq_amazon = 'PT' + (index + '').padStart(2, '0');
                }

                return attribute;
            });

            if (attributes.length > 0) {
                result[currentPK] = attributes;
            }

            return result;
        }, {});

        if (Object.keys(mutatedSelectedAssets).length === 0) {
            toast.error('MEDIA_ASSET_EXPORT.NO_ITEM');
            return;
        }

        runExport({
            payload: {
                namingPattern: selectedNamingPattern,
                items: {
                    ...mutatedSelectedAssets
                }
            }
        }, {
            onSuccess: () => {
                const filterAttributesMap =
                    selectedFilterAttributes.reduce((map, current) => {
                        map[current.value] = true;
                        return map;
                    }, {} as Record<string, boolean>);
                storeFilterAttributes(filterAttributesMap);

                toast.success('MEDIA_ASSET_EXPORT.STARTED');
                setOpen(false);
                onClose(true);
            }
        });
    };

    if (!dataModel) {
        return <></>;
    }

    return(
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent variant="large" onPointerDownOutside={e => e.preventDefault()} onInteractOutside={e => e.preventDefault()}>
                <DialogHeader>
                    <DialogHeaderDefault>{t('MEDIA_ASSET_EXPORT.LABEL')}</DialogHeaderDefault>
                </DialogHeader>
                <DialogBody padding="none">

                    <Toolbar
                        attributeFilterOptions={
                            Array.from(filterAttributes).map(attributeName => {
                                const attribute = dataModel.attribute(attributeName);
                                return {
                                    label: attribute?.label,
                                    value: attribute?.name
                                } as FacetedFilterOption;
                            })
                        }
                        selectedAttributeFilters={selectedFilterAttributes}
                        attributeFilterDispatch={selectedFilterAttributesDispatch}
                        areAllSelected={areAllSelected}
                        onSelectAll={() => onSelectAll()}
                        isShowingOnlySelected={isShowingOnlySelected}
                        onShowSelected={() => setIsShowingOnlySelected(!isShowingOnlySelected)}
                        galleryMode={galleryMode}
                        onGalleryModeChanged={setGalleryMode}
                    />

                    <div className="tw-flex tw-flex-col tw-px-6 tw-py-3 tw-overflow-auto tw-max-h-[60vh] tw-min-h-[60vh] tw-bg-light-gray">
                        {isPendingCreateSelection || isLoadingSelectedItems ?
                            <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center">
                                <Loader />
                            </div> :
                            selectedAssets &&
                            <Accordion type="multiple" value={openAccordionItems} onValueChange={setOpenAccordionItems}>
                                {Object.keys(selectedAssets).map(itemPrimaryKey => {
                                    const item = selectedAssets[itemPrimaryKey][0];
                                    const itemMediaAttributes = selectedAssets[itemPrimaryKey].filter(itemAttribute => shouldDisplayAttribute(itemAttribute));

                                    // Items with no available assets always have one
                                    // attribute with empty URL.
                                    let isEmpty = itemMediaAttributes.length === 0;
                                    if (itemMediaAttributes.length === 1 &&
                                        !itemMediaAttributes[0].__asset) {
                                        isEmpty = true;
                                    }

                                    if (isEmpty) {
                                        return (
                                            <ItemsMediaEmpty
                                                key={itemPrimaryKey}
                                                itemPrimaryKey={itemPrimaryKey}
                                                itemAttributes={item}
                                            />
                                        );
                                    } else {
                                        return (
                                            <ItemsMedia
                                                key={itemPrimaryKey}
                                                itemPrimaryKey={itemPrimaryKey}
                                                itemAttributes={itemMediaAttributes}
                                                onAssetSelect={onAssetSelect}
                                                galleryMode={galleryMode}
                                            />
                                        );
                                    }

                                })}
                            </Accordion>
                        }
                    </div>
                </DialogBody>

                <DialogFooter>
                    <Select value={selectedNamingPattern} onValueChange={setSelectedNamingPattern}>
                        <SelectTrigger className="tw-w-[200px] tw-truncate">
                            <SelectValue placeholder={t('MEDIA_ASSET_EXPORT.PATTERNS')} />
                        </SelectTrigger>
                        <SelectContent>
                            {namingPatterns && Object.keys(namingPatterns).map(namingPatternKey =>
                                <SelectItem
                                    key={namingPatternKey}
                                    value={namingPatterns[namingPatternKey]}
                                    title={namingPatterns[namingPatternKey]}>
                                    {namingPatternKey}
                                </SelectItem>
                            )}
                        </SelectContent>
                    </Select>
                    <Button
                        type="submit"
                        variant="default"
                        loading={isPendingRunExport}
                        disabled={isPendingRunExport}
                        onClick={onExport}>
                        {t('MEDIA_ASSET_EXPORT.EXECUTE')}
                    </Button>
                    <DialogClose asChild={true}>
                        <Button variant="secondary" type="submit">{t('CANCEL')}</Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

ExportMediaAssetDialog.displayName = 'ExportMediaAssetDialog';

export { ExportMediaAssetDialog, type ExportMediaAssetDialogProps };
